import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ─── Types (mirror backend/app/features/expansion/service.py) ────────────────

export interface ExpansionFlags {
  /** No New/Replacement contract at all — money columns are null. */
  no_contract: boolean;
  /** Contract has 0 contracted contacts — overage math is null. */
  no_allotment: boolean;
  /** No $/10k price on the contract — overage priced at $0. */
  no_block_price: boolean;
}

export interface ExpansionCompany {
  company_id: string;
  name: string | null;
  status: string;
  is_active: boolean;
  customers: number;
  emails: number;
  /** Whether this company's contacts are summed into the account total
   *  (false for Testing/Template — shown for review, not counted). */
  counted: boolean;
}

export interface ExpansionAccount {
  account_id: string;
  name: string | null;
  qb_id: number | null;
  tier: number | null;
  arr: number;
  companies_count: number;
  customers: number;
  emails: number;
  contract_type: string | null;
  contract_signed_at: string | null;
  uses_email_count: boolean;
  /** Governing contract allotment + post-contract upsell contacts. */
  contracted_contacts: number | null;
  upsell_contacts: number;
  /** The usage basis the contract prices on (emails or customers). */
  usage: number;
  base_fee: number | null;
  ten_k_more: number | null;
  overage_contacts: number | null;
  overage_blocks: number | null;
  overage_cost: number | null;
  /** What the contract says the monthly bill should be (base + overage). */
  contracted_total: number | null;
  /** Anchor-month billing on the base platform item. */
  currently_billed: number;
  /** contracted_total − currently_billed (the expansion gap). */
  amount_to_bill: number | null;
  csm_token: string | null;
  csm_label: string | null;
  flags: ExpansionFlags;
  /** Active, non-Sandbox companies under the account (counted + review). */
  companies: ExpansionCompany[];
}

export interface ExpansionResponse {
  month: string;
  refreshed_at: string;
  /** False when the warehouse role can't see the customers view (row access
   *  policy) — usage/overage columns are all zero and must not be trusted. */
  usage_data_available: boolean;
  accounts: ExpansionAccount[];
}

export interface CsmFilterOption {
  value: string;
  label: string;
  kind: "owner" | "user" | "partner";
  account_count: number;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

const KEY = ["expansion"] as const;

export function useExpansion(month?: string | null) {
  return useQuery({
    queryKey: [...KEY, "overage", month ?? "latest"],
    queryFn: () =>
      apiFetch<ExpansionResponse>(
        `/expansion/overage${month ? `?month=${encodeURIComponent(month)}` : ""}`,
      ),
  });
}

export function useExpansionCsmOptions() {
  return useQuery({
    queryKey: [...KEY, "csm-options"],
    queryFn: () => apiFetch<{ options: CsmFilterOption[] }>("/expansion/csm-options"),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ContactHistoryPoint {
  /** First of the month, YYYY-MM-01. */
  month: string;
  contacts: number;
  emails: number;
}

export interface ContactHistoryResponse {
  account_id: string;
  refreshed_at: string;
  points: ContactHistoryPoint[];
}

/** Monthly contact/email history for one account (counted companies only). */
export function useContactHistory(accountId: string | null) {
  return useQuery({
    queryKey: [...KEY, "history", accountId],
    queryFn: () =>
      apiFetch<ContactHistoryResponse>(
        `/expansion/history?account_id=${encodeURIComponent(accountId ?? "")}`,
      ),
    enabled: !!accountId,
  });
}
